import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { useBrandTokens } from '../../../shell/shared';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qPatientFilesByEncounter,
  qSymptomsByEncounter,
  qTreatmentsByEncounter,
  type PatientFileRow,
  type PatientSymptomEntryRow,
  type PatientTreatmentEntryRow,
} from '@helios/data-access';
import {
  useDermatologyAnalysis,
  type DermSuggestionEcho,
} from '../../../hooks/useDermatologyAnalysis';

/* ------------------------------------------------------------------ */
/*  DermatologyAiAnalyze (PRD.217 §6, §7.1, §7.2)                     */
/*                                                                    */
/*  Image-selection view: clinician picks 1–5 same-lesion images and  */
/*  clicks "Analyze Selected". While the Cloud Function runs we show  */
/*  an animated AnalyzingState; on success the AI Suggested card      */
/*  appears in the Diagnoses tab (via local INSERT into               */
/*  patient_diagnosis_suggestions → SyncWrite → other clients).       */
/* ------------------------------------------------------------------ */

const MIN_IMAGES = 1;
const MAX_IMAGES = 5;
const HELPER_TEXT = 'Select 1–5 images of the same lesion for AI analysis.';

interface Props {
  patientId: string;
  encounterId: string;
}

export function DermatologyAiAnalyze({ patientId, encounterId }: Props) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { user, claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const { db } = usePowerSyncDb();

  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );

  // Need existing symptom + treatment labels for case-insensitive dedupe.
  const { data: existingSymptoms = [] } = usePowerSyncQuery<PatientSymptomEntryRow>(
    qSymptomsByEncounter(encounterId),
  );
  const { data: existingTreatments = [] } = usePowerSyncQuery<PatientTreatmentEntryRow>(
    qTreatmentsByEncounter(encounterId),
  );

  const images = useMemo(() => fileRows.filter((f) => f.kind === 'image' && f.url), [fileRows]);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { status, error, enabled, analyse, reset } = useDermatologyAnalysis();
  const isAnalyzing = status === 'submitting';

  const selectedCount = selected.size;
  const overLimit = selectedCount > MAX_IMAGES;
  const canAnalyze =
    enabled && selectedCount >= MIN_IMAGES && selectedCount <= MAX_IMAGES && !isAnalyzing;

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    reset();
  }, [reset]);

  const handleAnalyze = useCallback(async () => {
    if (!canAnalyze) return;
    const result = await analyse({
      orgId,
      patientId,
      encounterId,
      encounterImageIds: [...selected],
    });
    if (result?.status === 'completed') {
      setSelected(new Set());
      if (db) {
        if (result.suggestion) {
          await applyAiDiagnosis(db, result.suggestion, userId);
        }
        if (result.symptoms && result.symptoms.length > 0 && result.suggestion) {
          const existingLabels = new Set(
            existingSymptoms.map((s) => (s.label ?? '').trim().toLowerCase()),
          );
          await applyAiSymptoms(db, {
            labels: result.symptoms,
            orgId: result.suggestion.orgId,
            clinicId: result.suggestion.clinicId,
            patientId: result.suggestion.patientId,
            encounterId: result.suggestion.encounterId,
            userId,
            existingLabels,
          });
        }
        if (result.treatments && result.treatments.length > 0 && result.suggestion) {
          const existingLabels = new Set(
            existingTreatments.map((t) => (t.label ?? '').trim().toLowerCase()),
          );
          await applyAiTreatments(db, {
            labels: result.treatments,
            orgId: result.suggestion.orgId,
            clinicId: result.suggestion.clinicId,
            patientId: result.suggestion.patientId,
            encounterId: result.suggestion.encounterId,
            userId,
            existingLabels,
          });
        }
      }
    }
  }, [
    analyse,
    canAnalyze,
    db,
    encounterId,
    existingSymptoms,
    existingTreatments,
    orgId,
    patientId,
    selected,
    userId,
  ]);

  if (!enabled) {
    return (
      <div style={styles.empty}>
        Dermatology AI is not configured for this environment. Set{' '}
        <code>VITE_DERMATOLOGY_AI_URL</code> to enable.
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div style={styles.empty}>
        No encounter images yet. Upload images from the <strong>Images</strong> view first, then
        return to AI Analyze.
      </div>
    );
  }

  const validationMessage = overLimit
    ? 'You can analyze up to 5 images at a time. Please select images of the same lesion.'
    : null;

  return (
    <div style={styles.wrapper}>
      {/* Helper + counter row */}
      <div style={styles.controls}>
        <div>
          <div style={styles.helper}>{HELPER_TEXT}</div>
          <div style={styles.counter}>
            {selectedCount} selected
            {selectedCount > 0 ? ` · ${MAX_IMAGES - selectedCount} remaining` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="ghost"
            size="md"
            onClick={clearSelection}
            disabled={selectedCount === 0 || isAnalyzing}
          >
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleAnalyze}
            disabled={!canAnalyze}
            style={{ minWidth: 160 }}
          >
            {isAnalyzing ? 'Analyzing…' : 'Analyze Selected'}
          </Button>
        </div>
      </div>

      {(validationMessage || error) && !isAnalyzing && (
        <div style={{ ...styles.errorBanner, ...(overLimit ? {} : styles.errorBannerSubtle) }}>
          {validationMessage ?? error}
        </div>
      )}

      {isAnalyzing ? (
        <AnalyzingState primary={primary} />
      ) : (
        <div style={styles.grid}>
          {images.map((file) => {
            const isSelected = selected.has(file.id);
            const disabled = !isSelected && selectedCount >= MAX_IMAGES;
            return (
              <button
                key={file.id}
                type="button"
                onClick={() => toggle(file.id)}
                aria-pressed={isSelected}
                disabled={disabled}
                style={{
                  ...styles.imageCard,
                  borderColor: isSelected ? primary : 'var(--border, #e5e7eb)',
                  boxShadow: isSelected ? `0 0 0 2px ${primary}33` : 'none',
                  opacity: disabled ? 0.45 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <img
                  src={file.url ?? undefined}
                  alt={file.filename ?? ''}
                  style={styles.imageImg}
                />
                <span
                  style={{
                    ...styles.checkbox,
                    background: isSelected ? primary : 'rgba(255,255,255,0.85)',
                    color: isSelected ? '#fff' : 'var(--text-muted, #6b7280)',
                    borderColor: isSelected ? primary : 'var(--border, #e5e7eb)',
                  }}
                  aria-hidden
                >
                  {isSelected ? <CheckIcon /> : null}
                </span>
                {file.filename && (
                  <span style={styles.filenamePill} title={file.filename}>
                    {file.filename}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Animated "Analyzing" state ─────────────────────────────────────────────
//
// Replaces the selection grid while the Cloud Function is running. Cycles
// status copy every couple of seconds so the user has feedback during the
// ~5s round-trip. All animations are CSS keyframes — no JS animation loop.

const STAGES = [
  'Reviewing your selected images',
  'Consulting the AI dermatology model',
  'Cross-checking lookalike conditions',
  'Preparing your suggestion',
];

function AnalyzingState({ primary }: { primary: string }) {
  const [stageIdx, setStageIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setStageIdx((i) => (i + 1) % STAGES.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <style>{`
        @keyframes derm-ai-pulse {
          0%, 100% { transform: scale(1); opacity: 0.85; }
          50% { transform: scale(1.18); opacity: 1; }
        }
        @keyframes derm-ai-ring {
          0% { transform: scale(1); opacity: 0.55; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes derm-ai-dot {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-3px); }
        }
        @keyframes derm-ai-fade {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div style={analyzingStyles.container}>
        <div style={analyzingStyles.iconWrap}>
          <span
            style={{
              ...analyzingStyles.ring,
              background: primary,
              animation: 'derm-ai-ring 1.6s ease-out infinite',
            }}
            aria-hidden
          />
          <span
            style={{
              ...analyzingStyles.ring,
              background: primary,
              animation: 'derm-ai-ring 1.6s 0.5s ease-out infinite',
            }}
            aria-hidden
          />
          <span
            style={{
              ...analyzingStyles.iconBubble,
              background: primary,
              animation: 'derm-ai-pulse 1.6s ease-in-out infinite',
            }}
          >
            <SparkleIcon />
          </span>
        </div>

        <div style={analyzingStyles.title}>Analyzing dermatology images</div>
        <div
          key={stageIdx}
          style={{
            ...analyzingStyles.stage,
            animation: 'derm-ai-fade 320ms ease-out',
          }}
        >
          {STAGES[stageIdx]}…
        </div>

        <div style={analyzingStyles.dots} aria-hidden>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                ...analyzingStyles.dot,
                background: primary,
                animation: `derm-ai-dot 1.4s ${i * 0.16}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>

        <div style={analyzingStyles.hint}>This usually takes a few seconds.</div>
      </div>
    </>
  );
}

// ─── Auto-apply the AI diagnosis ────────────────────────────────────────────
//
// PRD.217 — instead of presenting an Accept/Edit/Dismiss card, the AI
// diagnosis is auto-added as a regular chip in the Diagnoses list with an
// inline "AI Suggested" badge. Removal is a single click on the chip's X
// (same UX as a clinician-typed diagnosis).
//
// Two rows are written, both via `db.execute` so they flow through
// PowerSync → SyncWrite → Postgres → other clients:
//   1. `patient_diagnosis_entries` — the visible chip
//   2. `patient_diagnosis_suggestions` (status='accepted', linked via
//      `accepted_diagnosis_entry_id`) — keeps the AI audit trail and is
//      what the chip list reads to decide whether to show the badge.
async function applyAiDiagnosis(
  db: NonNullable<ReturnType<typeof usePowerSyncDb>['db']>,
  s: DermSuggestionEcho,
  userId: string,
): Promise<void> {
  const entryId = crypto.randomUUID();
  const now = new Date().toISOString();

  try {
    await db.execute(
      `INSERT INTO patient_diagnosis_entries
        (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at, updated_by)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        entryId,
        s.orgId,
        s.clinicId,
        s.patientId,
        s.encounterId,
        s.diagnosisText,
        now,
        now,
        userId || null,
      ],
    );

    await db.execute(
      `INSERT OR REPLACE INTO patient_diagnosis_suggestions
        (id, org_id, clinic_id, patient_id, encounter_id,
         ai_analysis_run_id, source_provider, diagnosis_text,
         condition, severity, confidence, status,
         accepted_label, accepted_diagnosis_entry_id, accepted_by, accepted_at,
         created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        s.id,
        s.orgId,
        s.clinicId,
        s.patientId,
        s.encounterId,
        s.aiAnalysisRunId,
        s.sourceProvider,
        s.diagnosisText,
        s.condition,
        s.severity,
        s.confidence,
        'accepted',
        s.diagnosisText,
        entryId,
        userId || null,
        now,
        s.createdAt,
        s.updatedAt,
      ],
    );
  } catch (err) {
    console.warn('[dermatology-ai] Auto-apply AI diagnosis failed:', err);
  }
}

// ─── Auto-apply AI symptoms ─────────────────────────────────────────────────
//
// Inserts the top primary symptoms returned by Siksha into
// `patient_symptom_entries` with `source='ai'` so the chips render with the
// "AI Suggested" badge. Dedupe is case-insensitive against the encounter's
// existing symptom labels so re-running Analyze doesn't pile up duplicates.
async function applyAiSymptoms(
  db: NonNullable<ReturnType<typeof usePowerSyncDb>['db']>,
  input: {
    labels: readonly string[];
    orgId: string;
    clinicId: string | null;
    patientId: string;
    encounterId: string;
    userId: string;
    existingLabels: Set<string>;
  },
): Promise<void> {
  const now = new Date().toISOString();
  for (const raw of input.labels) {
    const label = raw.trim();
    if (!label) continue;
    if (input.existingLabels.has(label.toLowerCase())) continue;
    try {
      await db.execute(
        `INSERT INTO patient_symptom_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, source, created_at, updated_at, updated_by)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          input.orgId,
          input.clinicId,
          input.patientId,
          input.encounterId,
          label,
          'ai',
          now,
          now,
          input.userId || null,
        ],
      );
      input.existingLabels.add(label.toLowerCase());
    } catch (err) {
      console.warn('[dermatology-ai] Auto-apply AI symptom failed:', label, err);
    }
  }
}

// ─── Auto-apply AI treatments ───────────────────────────────────────────────
//
// Inserts the top common-agent treatments returned by Siksha into
// `patient_treatment_entries` with `source='ai'`. Reference-only per
// PRD.217 §16.5/16.6 — these rows are NEVER promoted to prescriptions or
// billing items. Dedupe is case-insensitive against the encounter's
// existing treatment labels so re-running Analyze doesn't pile up duplicates.
async function applyAiTreatments(
  db: NonNullable<ReturnType<typeof usePowerSyncDb>['db']>,
  input: {
    labels: readonly string[];
    orgId: string;
    clinicId: string | null;
    patientId: string;
    encounterId: string;
    userId: string;
    existingLabels: Set<string>;
  },
): Promise<void> {
  const now = new Date().toISOString();
  for (const raw of input.labels) {
    const label = raw.trim();
    if (!label) continue;
    if (input.existingLabels.has(label.toLowerCase())) continue;
    try {
      await db.execute(
        `INSERT INTO patient_treatment_entries
          (id, org_id, clinic_id, patient_id, encounter_id, label, source, created_at, updated_at, updated_by)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          input.orgId,
          input.clinicId,
          input.patientId,
          input.encounterId,
          label,
          'ai',
          now,
          now,
          input.userId || null,
        ],
      );
      input.existingLabels.add(label.toLowerCase());
    } catch (err) {
      console.warn('[dermatology-ai] Auto-apply AI treatment failed:', label, err);
    }
  }
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
    </svg>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  wrapper: {
    padding: 'var(--sp-5, 20px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-4, 16px)',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 'var(--sp-4, 16px)',
    flexWrap: 'wrap',
  },
  helper: {
    fontSize: 'var(--font-size-sm, 12px)',
    fontWeight: 500,
    color: 'var(--text, #111827)',
  },
  counter: {
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-muted, #6b7280)',
    marginTop: 2,
  },
  errorBanner: {
    padding: 'var(--sp-3, 12px)',
    borderRadius: 'var(--radius-md, 10px)',
    background: '#fee2e2',
    color: '#991b1b',
    fontSize: 'var(--font-size-sm, 12px)',
  },
  errorBannerSubtle: {
    background: '#fef3c7',
    color: '#92400e',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 'var(--sp-3, 12px)',
  },
  imageCard: {
    position: 'relative',
    border: '2px solid',
    borderRadius: 'var(--radius-md, 10px)',
    padding: 0,
    background: 'var(--bg-surface, #fff)',
    aspectRatio: '1',
    overflow: 'hidden',
    transition: 'box-shadow 150ms, border-color 150ms, opacity 150ms',
  },
  imageImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  checkbox: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1.5px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filenamePill: {
    position: 'absolute',
    bottom: 6,
    left: 6,
    right: 6,
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm, 8px)',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  empty: {
    padding: 'var(--sp-8, 32px) var(--sp-5, 20px)',
    textAlign: 'center',
    color: 'var(--text-muted, #6b7280)',
    fontSize: 'var(--font-size-sm, 12px)',
  },
};

const analyzingStyles: Record<string, CSSProperties> = {
  container: {
    padding: 'var(--sp-8, 32px) var(--sp-5, 20px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--sp-3, 12px)',
    borderRadius: 'var(--radius-lg, 14px)',
    background: 'var(--bg-surface-2, #f8fafc)',
    border: '1px solid var(--border, #e5e7eb)',
    minHeight: 260,
    justifyContent: 'center',
  },
  iconWrap: {
    position: 'relative',
    width: 72,
    height: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 'var(--sp-2, 8px)',
  },
  ring: {
    position: 'absolute',
    width: 56,
    height: 56,
    borderRadius: '50%',
    opacity: 0.4,
    pointerEvents: 'none',
  },
  iconBubble: {
    position: 'relative',
    width: 56,
    height: 56,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 8px 24px -8px rgba(37, 99, 235, 0.4)',
  },
  title: {
    fontSize: 'var(--font-size-md, 14px)',
    fontWeight: 600,
    color: 'var(--text, #111827)',
  },
  stage: {
    fontSize: 'var(--font-size-sm, 12px)',
    color: 'var(--text-muted, #6b7280)',
    minHeight: 18,
    textAlign: 'center',
  },
  dots: {
    display: 'flex',
    gap: 6,
    marginTop: 'var(--sp-1, 4px)',
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    display: 'inline-block',
  },
  hint: {
    marginTop: 'var(--sp-2, 8px)',
    fontSize: 'var(--font-size-xs, 11px)',
    color: 'var(--text-placeholder, #9ca3af)',
  },
};
